import React, { useEffect, useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Alert, Button, Input, Modal, notification, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./modal_data_work_order.css";
import axios from 'axios';
import { useAuth } from "../../../context/AuthContext";
import useSWR from 'swr';

interface DataType {
    key: React.Key;
    id: number;
    work_order_no: string;
    bill_to_name: string;
    bill_to_code: string;
    work_order_date: string;
    service_advisor: string;
    warehouse: string;
    warehouse_name: string;
    status: string;
    has_mechanic?: boolean;
}

interface ModalDataWorkOrderProps {
    onRowSelect: (record: DataType) => void;
    isImporting?: boolean;

}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);

const ModalDataWorkOrder: React.FC<ModalDataWorkOrderProps> = ({ onRowSelect, isImporting }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [sortField, setSortField] = useState<string>('work_order_date');
    const [sortOrder, setSortOrder] = useState<string>('descend');
    const [isRowProcessing, setIsRowProcessing] = useState(false);
    const { userData } = useAuth();

    const { data: workOrderData, error, isLoading } = useSWR(
        `${process.env.REACT_APP_BACKEND_URL}/api/supply-slip/work-orders?search=${searchText}&searchColumn=${searchedColumn}&sortBy=${sortField}&sortOrder=${sortOrder}&warehouse=${userData?.warehouse_code || ''}`,
        fetcher
    );

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
    };


    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        if (sorter && sorter.field) {
            setSortField(sorter.field);
            setSortOrder(sorter.order || 'descend');
        }
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Work Order No',
            dataIndex: 'work_order_no',
            key: 'work_order_no',
            ...getColumnSearchProps('work_order_no'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer Name',
            dataIndex: 'bill_to_name',
            key: 'bill_to_name',
            ...getColumnSearchProps('bill_to_name'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer Code',
            dataIndex: 'bill_to_code',
            key: 'bill_to_code',
            ...getColumnSearchProps('bill_to_code'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Service Date',
            dataIndex: 'work_order_date',
            key: 'work_order_date',
            ...getColumnSearchProps('work_order_date'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Advisor',
            dataIndex: 'service_advisor',
            key: 'service_advisor',
            ...getColumnSearchProps('service_advisor'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse_name',
            key: 'warehouse_name',
            ...getColumnSearchProps('warehouse_name'),
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
    ];

    return (

        <div id="itgi" style={transformStyle}>
            <div className="gjs-grid-row" id="iz1r">
                <div className="gjs-grid-column" id="io41">
                    {error && <Alert message="Error" description="Gagal mengambil data Work Order." type="error" showIcon style={{ marginBottom: 16 }} />}
                    <Table
                        columns={columns}
                        dataSource={workOrderData?.map((item: any) => ({ ...item, key: item.id })) || []}
                        loading={isLoading || isRowProcessing || !!isImporting}
                        pagination={{ pageSize: 10 }}
                        onChange={handleTableChange}
                        onRow={(record) => ({
                            style: { backgroundColor: !record.has_mechanic ? '#ffe7ba' : undefined },
                            onDoubleClick: () => {
                                if (isRowProcessing || isImporting) return;
                                onRowSelect(record);
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModalDataWorkOrder;